import React, { useState, useMemo, useCallback } from 'react';
import { HiOutlineCheck, HiOutlineExclamation, HiOutlineChevronDown, HiOutlinePlus, HiOutlineTrash, HiOutlineLockClosed } from 'react-icons/hi';
import toast from 'react-hot-toast';
import api from '../../utils/api';

/**
 * One Q AI Suggested Action, rendered entirely from a server descriptor.
 *
 * This component knows nothing about receipts, purchase orders or journals. It
 * renders whatever fields the server said the action has, lets the user edit
 * them, and POSTs the result to /ai/actions/execute. Adding a new action type is
 * a server-only change — see server/utils/aiActions.js.
 *
 * That is the point of the rewrite. The previous shape had one bespoke card
 * component per action type, each with its own hand-written creation handler
 * that resolved names and assembled a document in the browser. Six near-copies
 * of the same layout, six places for the execution logic to drift from the
 * models it duplicated, and no way for a user to correct a single wrong digit
 * without asking Q again from scratch.
 */

const TIERS = {
  navigate: { label: 'Opens a form',        tone: 'slate' },
  draft:    { label: 'Creates a draft',     tone: 'emerald' },
  post:     { label: 'Posts immediately',   tone: 'rose' },
};

const palette = (color, isDark) => ({
  teal:    { border: isDark ? 'border-teal-400/25'    : 'border-teal-200/80',    bg: isDark ? 'bg-teal-500/10'    : 'bg-teal-50/90',    label: isDark ? 'text-teal-300'    : 'text-teal-700',    btn: 'bg-teal-600 hover:bg-teal-700' },
  sky:     { border: isDark ? 'border-sky-400/25'     : 'border-sky-200/80',     bg: isDark ? 'bg-sky-500/10'     : 'bg-sky-50/90',     label: isDark ? 'text-sky-300'     : 'text-sky-700',     btn: 'bg-sky-600 hover:bg-sky-700' },
  violet:  { border: isDark ? 'border-violet-400/25'  : 'border-violet-200/80',  bg: isDark ? 'bg-violet-500/10'  : 'bg-violet-50/90',  label: isDark ? 'text-violet-300'  : 'text-violet-700',  btn: 'bg-violet-600 hover:bg-violet-700' },
  amber:   { border: isDark ? 'border-amber-400/25'   : 'border-amber-200/80',   bg: isDark ? 'bg-amber-500/10'   : 'bg-amber-50/90',   label: isDark ? 'text-amber-300'   : 'text-amber-700',   btn: 'bg-amber-600 hover:bg-amber-700' },
  rose:    { border: isDark ? 'border-rose-400/25'    : 'border-rose-200/80',    bg: isDark ? 'bg-rose-500/10'    : 'bg-rose-50/90',    label: isDark ? 'text-rose-300'    : 'text-rose-700',    btn: 'bg-rose-600 hover:bg-rose-700' },
  emerald: { border: isDark ? 'border-emerald-400/25' : 'border-emerald-200/80', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50/90', label: isDark ? 'text-emerald-300' : 'text-emerald-700', btn: 'bg-emerald-600 hover:bg-emerald-700' },
}[color] || palette('teal', isDark));

const DISMISS_REASONS = [
  'Wrong party',
  'Wrong amount',
  'Wrong date',
  'Already recorded',
  "I wouldn't do this",
];

// Mirrors the server's convention in utils/aiActions.js: an entity field named
// `<base>_name` carries its resolved row id in `<base>_id`.
const baseKey = (key) => key.replace(/_name$/, '');

// Seed edit state from the descriptor: entity fields carry the RESOLVED name for
// display and the resolved id alongside, so confirming without editing sends the
// exact row the server picked rather than re-resolving a string.
const seedState = (fields) => {
  const state = {};
  for (const field of fields) {
    state[field.key] = field.value ?? '';
    if (field.input === 'entity') state[`${baseKey(field.key)}_id`] = field.resolved?.id ?? null;
  }
  return state;
};

const inputClass = (isDark) =>
  `w-full min-w-0 rounded-md border px-2 py-1 text-xs text-right tabular-nums focus:outline-none focus:ring-1 ${
    isDark
      ? 'border-white/10 bg-slate-950/40 text-slate-100 focus:ring-emerald-400/50'
      : 'border-slate-200 bg-white text-slate-900 focus:ring-emerald-500/40'
  }`;

const FieldRow = ({ field, state, onChange, isDark, disabled }) => {
  const value = state[field.key] ?? '';
  const idKey = `${baseKey(field.key)}_id`;

  const set = (v) => onChange({ [field.key]: v });

  const control = () => {
    if (field.input === 'select') {
      return (
        <select
          value={value}
          disabled={disabled}
          onChange={(e) => set(e.target.value)}
          className={inputClass(isDark)}
        >
          {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }

    if (field.input === 'entity') {
      const candidates = field.candidates || [];
      return (
        <div className="flex flex-col items-end gap-1 w-full">
          <input
            type="text"
            value={value}
            disabled={disabled}
            // Typing a new name drops the resolved id — the server re-resolves
            // the text. Keeping a stale id here is how you pay the wrong supplier.
            onChange={(e) => onChange({ [field.key]: e.target.value, [idKey]: null })}
            className={inputClass(isDark)}
          />
          {candidates.length > 1 && (
            <select
              value={state[idKey] ?? ''}
              disabled={disabled}
              onChange={(e) => {
                const picked = candidates.find((c) => String(c.id) === e.target.value);
                onChange({ [idKey]: picked ? picked.id : null, [field.key]: picked ? picked.name : value });
              }}
              className={`${inputClass(isDark)} text-[11px]`}
            >
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>{c.code ? `${c.code} — ${c.name}` : c.name}</option>
              ))}
            </select>
          )}
        </div>
      );
    }

    return (
      <input
        type={field.input === 'date' ? 'date' : (field.input === 'money' || field.input === 'number' ? 'number' : 'text')}
        step={field.input === 'money' ? '0.01' : undefined}
        value={value}
        disabled={disabled}
        onChange={(e) => set(e.target.value)}
        className={inputClass(isDark)}
      />
    );
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] items-center gap-3 px-3 py-1.5">
      <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
        {field.label}{field.required ? '' : <span className="opacity-50"> (optional)</span>}
      </span>
      {control()}
    </div>
  );
};

const SuggestedActionCard = ({ descriptor, isDark, onCompleted, onNavigate }) => {
  const c = palette(descriptor.color, isDark);
  const tier = TIERS[descriptor.tier] || TIERS.draft;

  const [header, setHeader] = useState(() => seedState(descriptor.fields || []));
  const [lines, setLines] = useState(() => (descriptor.lines || []).map(seedState));
  const [busy, setBusy] = useState(false);
  // A descriptor may arrive already spent. The Dashboard's insights are rebuilt
  // from live probes on every load, so the server marks a suggestion the user
  // already acted on rather than handing back a live button for work that is
  // done — see routes/ai.js and migration 237. Seeding `result` is all it takes:
  // every "already created" branch below is the one the execute path uses.
  const [result, setResult] = useState(() => (descriptor.completed
    ? { number: descriptor.completed.number, route: descriptor.completed.route, prior: true }
    : null));
  const [dismissed, setDismissed] = useState(false);
  const [showDismiss, setShowDismiss] = useState(false);
  const [showEffect, setShowEffect] = useState(false);

  const lineSpec = descriptor.line_spec;

  const payload = useMemo(() => {
    const out = { ...descriptor.payload, ...header };
    if (lineSpec) out[lineSpec.key] = lines;
    return out;
  }, [descriptor.payload, header, lines, lineSpec]);

  const confirm = useCallback(async () => {
    setBusy(true);
    try {
      const res = await api.post('/ai/actions/execute', {
        action_id: descriptor.action_id,
        type: descriptor.type,
        payload,
      });
      setResult({ number: res.data?.number, route: res.data?.route });
      toast.success(res.data?.message || 'Done');
      onCompleted?.(descriptor.action_id, res.data);
    } catch (err) {
      const data = err?.response?.data;
      // A 409 means this suggestion already produced a document. Show which one
      // rather than a failure — the user got what they asked for, once.
      if (err?.response?.status === 409) {
        setResult({ number: data?.result?.number, route: null, duplicate: true });
        toast(data?.message || 'Already created.', { icon: 'ℹ️' });
      } else {
        toast.error(data?.message || err.message || 'Could not carry out that action.');
      }
    } finally {
      setBusy(false);
    }
  }, [descriptor, payload, onCompleted]);

  const dismiss = useCallback(async (reason) => {
    setDismissed(true);
    setShowDismiss(false);
    try {
      await api.post('/ai/actions/dismiss', {
        action_id: descriptor.action_id,
        type: descriptor.type,
        payload,
        reason,
      });
    } catch { /* telemetry only — never block the UI */ }
  }, [descriptor, payload]);

  if (dismissed && !result) {
    return (
      <div className={`mt-2 rounded-xl border px-3 py-2 text-xs ${isDark ? 'border-white/10 text-slate-500' : 'border-slate-200 text-slate-400'}`}>
        Suggestion dismissed.
      </div>
    );
  }

  const hasErrors = descriptor.errors?.length > 0;
  const canConfirm = descriptor.allowed && !hasErrors && !result;

  // Only dated when the card is reporting an earlier session's work. Stamping
  // "Created 11 Aug" on something the user created ten seconds ago reads as a
  // record rather than a confirmation.
  const completedOn = result?.prior && descriptor.completed?.at
    ? new Date(descriptor.completed.at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    : null;

  return (
    <div className={`mt-2 rounded-xl border ${c.border} ${c.bg} p-3 shadow-sm`}>
      {/* Title bar */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={`text-xs font-semibold uppercase tracking-wide ${c.label}`}>
            Suggested action · {descriptor.title}
          </div>
          {!result && descriptor.subtitle && (
            <div className={`mt-0.5 text-sm ${isDark ? 'text-slate-50' : 'text-slate-900'}`}>{descriptor.subtitle}</div>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-1.5">
          {result ? (
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${isDark ? 'bg-emerald-400/15 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
              <HiOutlineCheck className="h-3 w-3" /> {result.duplicate ? 'Already created' : `Created${completedOn ? ` ${completedOn}` : ''}`}
            </span>
          ) : (
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${isDark ? 'bg-white/[0.06] text-slate-400' : 'bg-white/70 text-slate-500'}`}>
              {tier.label}
            </span>
          )}
        </div>
      </div>

      {/* Blocking reasons — a permission the user lacks, or a party we couldn't find */}
      {hasErrors && (
        <div className={`mt-2 rounded-lg px-3 py-2 text-xs ${isDark ? 'bg-rose-500/10 text-rose-300' : 'bg-rose-50 text-rose-700'}`}>
          <div className="flex items-start gap-1.5">
            {descriptor.allowed ? <HiOutlineExclamation className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                                : <HiOutlineLockClosed className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />}
            <div className="space-y-0.5">
              {descriptor.errors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          </div>
        </div>
      )}

      {/* Non-blocking cautions — an ambiguous match, an unusually large amount */}
      {!result && descriptor.warnings?.length > 0 && (
        <div className={`mt-2 rounded-lg px-3 py-2 text-xs ${isDark ? 'bg-amber-500/10 text-amber-300' : 'bg-amber-50 text-amber-800'}`}>
          {descriptor.warnings.map((w, i) => <div key={i}>{w}</div>)}
        </div>
      )}

      {/* Header fields */}
      <div className={`mt-3 overflow-hidden rounded-lg border ${isDark ? 'border-white/10 bg-slate-950/30' : 'border-white/70 bg-white/80'}`}>
        <div className={`divide-y ${isDark ? 'divide-white/[0.06]' : 'divide-slate-100'}`}>
          {(descriptor.fields || []).map((field) => (
            <FieldRow
              key={field.key}
              field={field}
              state={header}
              onChange={(patch) => setHeader((s) => ({ ...s, ...patch }))}
              isDark={isDark}
              disabled={busy || !!result || !descriptor.allowed}
            />
          ))}
        </div>

        {/* Editable line grid */}
        {lineSpec && (
          <div className={`border-t ${isDark ? 'border-white/[0.06]' : 'border-slate-100'}`}>
            <div className={`px-3 pt-2 text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              {lineSpec.label}
            </div>
            {lines.map((line, i) => (
              <div key={i} className={`relative pb-1 ${i > 0 ? `border-t ${isDark ? 'border-white/[0.06]' : 'border-slate-100'}` : ''}`}>
                {lineSpec.fields.map((specField) => {
                  const descField = descriptor.lines?.[i]?.find((d) => d.key === specField.key) || specField;
                  return (
                    <FieldRow
                      key={specField.key}
                      field={descField}
                      state={line}
                      onChange={(patch) => setLines((rows) => rows.map((r, ri) => (ri === i ? { ...r, ...patch } : r)))}
                      isDark={isDark}
                      disabled={busy || !!result || !descriptor.allowed}
                    />
                  );
                })}
                {!result && descriptor.allowed && lines.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setLines((rows) => rows.filter((_, ri) => ri !== i))}
                    className={`absolute right-2 top-1 rounded p-1 ${isDark ? 'text-slate-600 hover:text-rose-400' : 'text-slate-300 hover:text-rose-500'}`}
                    title="Remove line"
                  >
                    <HiOutlineTrash className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
            {!result && descriptor.allowed && (
              <button
                type="button"
                onClick={() => setLines((rows) => [...rows, seedState(lineSpec.fields.map((sf) => ({ ...sf, value: '' })))])}
                className={`m-2 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium ${isDark ? 'text-slate-400 hover:bg-white/[0.06]' : 'text-slate-500 hover:bg-slate-100'}`}
              >
                <HiOutlinePlus className="h-3 w-3" /> Add line
              </button>
            )}
          </div>
        )}

        {/* Server-derived read-only lines (e.g. a reversal read off the original journal) */}
        {descriptor.display_lines?.length > 0 && (
          <div className={`border-t ${isDark ? 'border-white/[0.06] divide-white/[0.06]' : 'border-slate-100 divide-slate-100'} divide-y`}>
            {descriptor.display_lines.map((line, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-xs">
                <div className="min-w-0">
                  <div className={`font-medium ${isDark ? 'text-slate-50' : 'text-slate-900'}`}>{line.label}</div>
                  {line.sublabel && <div className={`text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{line.sublabel}</div>}
                </div>
                <div className={`whitespace-nowrap font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{line.value}</div>
              </div>
            ))}
          </div>
        )}

        {descriptor.totals?.length > 0 && (
          <div className={`flex justify-between gap-3 border-t px-3 py-2 text-[11px] font-semibold ${isDark ? 'border-white/[0.06] text-slate-300' : 'border-slate-100 text-slate-700'}`}>
            {descriptor.totals.map((t, i) => <span key={i}>{t.label}: {t.value}</span>)}
          </div>
        )}
      </div>

      {/* "What this will do" — the difference between a copilot and a form filler */}
      {descriptor.effect?.length > 0 && !result && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowEffect((v) => !v)}
            className={`inline-flex items-center gap-1 text-[11px] font-medium ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <HiOutlineChevronDown className={`h-3 w-3 transition-transform ${showEffect ? 'rotate-180' : ''}`} />
            What this will do
          </button>
          {showEffect && (
            <ul className={`mt-1 space-y-0.5 pl-4 text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {descriptor.effect.map((e, i) => <li key={i} className="list-disc">{e}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* Actions */}
      {result ? (
        result.route && (
          <button
            type="button"
            onClick={() => onNavigate?.(result.route)}
            className={`mt-3 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold ${isDark ? 'bg-white/10 text-slate-100 hover:bg-white/[0.16]' : 'bg-white text-slate-700 shadow-sm hover:bg-slate-50'}`}
          >
            Open {result.number || 'it'}
          </button>
        )
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {canConfirm && (
            <button
              type="button"
              onClick={confirm}
              disabled={busy}
              className={`inline-flex items-center gap-2 rounded-lg ${c.btn} px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {busy ? 'Working…' : descriptor.confirm_label}
            </button>
          )}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowDismiss((v) => !v)}
              disabled={busy}
              className={`rounded-lg px-3 py-2 text-xs font-medium transition-colors ${isDark ? 'text-slate-400 hover:bg-white/[0.06] hover:text-slate-200' : 'text-slate-500 hover:bg-white/70 hover:text-slate-700'}`}
            >
              No thanks
            </button>
            {showDismiss && (
              <div className={`absolute bottom-full left-0 z-10 mb-1 w-44 overflow-hidden rounded-lg border shadow-lg ${isDark ? 'border-white/10 bg-slate-900' : 'border-slate-200 bg-white'}`}>
                <div className={`px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  Why not?
                </div>
                {DISMISS_REASONS.map((reason) => (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => dismiss(reason)}
                    className={`block w-full px-3 py-1.5 text-left text-xs ${isDark ? 'text-slate-300 hover:bg-white/[0.06]' : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    {reason}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => dismiss(null)}
                  className={`block w-full border-t px-3 py-1.5 text-left text-xs ${isDark ? 'border-white/10 text-slate-500 hover:bg-white/[0.06]' : 'border-slate-100 text-slate-400 hover:bg-slate-50'}`}
                >
                  Just dismiss
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuggestedActionCard;
